import { useState, useRef, useEffect } from 'react';
import { Upload, CreditCard, XCircle } from 'lucide-react';
import type { Order, OrderRoutes } from '../../types/orders';
import { getStatusStyle } from '../../utils/orderStatus';
import { formatPrice } from '../../utils/format';

interface OrderCardProps {
  order: Order;
  routes: OrderRoutes;
  onCancel: (order: Order) => void;
}

export function OrderCard({ order, routes, onCancel }: OrderCardProps) {
  const statusStyle = getStatusStyle(order.status);
  const route = (tpl: string) => tpl.replace('__ID__', String(order.id));

  // Dropdown actions
  const dropdownActions: { label: string; icon?: React.ReactNode; href?: string; onClick?: () => void; danger?: boolean }[] = [];

  if (order.is_transfer_pending) {
    dropdownActions.push({
      label: 'Subir comprobante',
      icon: <Upload className="h-4 w-4" />,
      href: route(routes.upload_receipt),
    });
  }

  if (order.can_be_paid_online) {
    dropdownActions.push({
      label: 'Cambiar forma de pago',
      icon: <CreditCard className="h-4 w-4" />,
      href: route(routes.pay),
    });
  }

  if (order.is_user_cancellable) {
    dropdownActions.push({
      label: 'Cancelar pedido',
      icon: <XCircle className="h-4 w-4" />,
      onClick: () => onCancel(order),
      danger: true,
    });
  }

  return (
    <div className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md sm:p-5">
      {/* Header: Status + Total */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${statusStyle}`}>
            {order.status_name}
          </span>
          {order.is_reposition && (
            <span className="inline-block rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
              Reposición
            </span>
          )}
        </div>
        {!order.is_reposition && (
          <span className="text-lg font-bold text-gray-800">
            {formatPrice(order.total)}
          </span>
        )}
      </div>

      {/* Reposition subtitle */}
      {order.is_reposition && order.source_order_id && (
        <p className="mb-2 text-xs text-blue-600">
          Reposición del pedido #{order.source_order_id}
        </p>
      )}

      {/* Dates & Payment */}
      <div className="mb-3 flex gap-5 border-b border-gray-100 pb-3">
        <DateTimeItem label="Pedido" value={order.created_at} />
        {order.delivery_date && <DateTimeItem label="Entrega" value={order.delivery_date} />}
        {order.payment_method_name && <DateItem label="Pago" value={order.payment_method_name} />}
      </div>

      {/* Products */}
      <ul className="mb-3 list-none space-y-0.5 p-0">
        {order.products.map((p, i) => (
          <li key={i} className="flex items-baseline gap-1.5 text-[13px]">
            <span className="min-w-[24px] text-xs text-gray-400">{p.quantity}x</span>
            <span className="text-gray-800">{p.product_name}</span>
          </li>
        ))}
        {order.products_count > 3 && (
          <li className="mt-1 text-xs font-medium text-green-600">
            +{order.products_count - 3} productos más
          </li>
        )}
      </ul>

      {/* Address */}
      <div className="mb-4 flex items-start gap-1.5 rounded-lg bg-gray-50 px-2.5 py-2">
        <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
        </svg>
        <span className="text-[13px] leading-snug text-gray-500">{order.address}</span>
      </div>

      {/* Actions */}
      <div className="mt-auto flex gap-2">
        <a
          href={route(routes.show)}
          className="inline-flex flex-1 items-center justify-center rounded-lg bg-green-600 px-3 py-2.5 text-[13px] font-semibold text-white no-underline transition-colors hover:bg-green-700 hover:text-white"
        >
          Ver detalle
        </a>
        {!order.is_reposition && (
          <a
            href={route(routes.repeat)}
            className="inline-flex flex-1 items-center justify-center rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 text-[13px] font-semibold text-gray-700 no-underline transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            Repetir
          </a>
        )}
        {dropdownActions.length > 0 && <ActionsDropdown actions={dropdownActions} />}
      </div>
    </div>
  );
}

function ActionsDropdown({ actions }: { actions: { label: string; icon?: React.ReactNode; href?: string; onClick?: () => void; danger?: boolean }[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (actions.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex h-full items-center justify-center rounded-lg border border-gray-300 bg-gray-50 px-2.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
        aria-label="Más acciones"
      >
        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <circle cx="10" cy="4" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="10" cy="16" r="1.5" />
        </svg>
      </button>

      {open && (
        <div className="absolute bottom-full right-0 z-10 mb-1 min-w-[210px] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {actions.map((action, i) =>
            action.href ? (
              <a
                key={i}
                href={action.href}
                className={`flex w-full items-center gap-2.5 px-3.5 py-2 text-left text-[13px] no-underline transition-colors hover:bg-gray-50 ${
                  action.danger ? 'text-red-600 hover:text-red-600' : 'text-gray-700 hover:text-gray-700'
                }`}
                onClick={() => setOpen(false)}
              >
                {action.icon && <span className="flex-shrink-0 opacity-70">{action.icon}</span>}
                {action.label}
              </a>
            ) : (
              <button
                key={i}
                type="button"
                className={`flex w-full cursor-pointer items-center gap-2.5 border-0 bg-transparent px-3.5 py-2 text-left text-[13px] transition-colors hover:bg-gray-50 ${
                  action.danger ? 'text-red-600' : 'text-gray-700'
                }`}
                onClick={() => { setOpen(false); action.onClick?.(); }}
              >
                {action.icon && <span className="flex-shrink-0 opacity-70">{action.icon}</span>}
                {action.label}
              </button>
            )
          )}
        </div>
      )}
    </div>
  );
}

function DateItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">{label}</span>
      <span className="text-[13px] font-medium text-gray-800">{value}</span>
    </div>
  );
}

function DateTimeItem({ label, value }: { label: string; value: string }) {
  const parts = value.split(' ');
  const date = parts[0] || value;
  const time = parts[1] || '';

  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wider text-gray-400">{label}</span>
      <span className="text-[13px] font-medium text-gray-800">{date}</span>
      {time && <span className="text-[12px] text-gray-500">{time}</span>}
    </div>
  );
}

